import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, KeyRound, Eye, Unplug, Plug, Check, X, ServerCog, Database, ScanEye, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WaitlistCTA } from '../components/WaitlistCTA';
/* ── Data ─────────────────────────────────────────────────────────── */
const CONTROL_PRINCIPLES = [{
  n: '01',
  icon: Eye,
  title: 'You approve every action',
  body: 'Giga never moves money, sends an external email, or pushes code without your explicit sign-off in Slack.'
}, {
  n: '02',
  icon: Unplug,
  title: 'Disconnect at any time',
  body: 'Pause a user, kill a running task, or revoke any integration in one click — no support ticket required.'
}, {
  n: '03',
  icon: Plug,
  title: 'Connect only what you need',
  body: 'Admins decide which tools Giga can reach, who can use them, and at what scope.'
}];
type CertStatus = 'Certified' | 'Aligned' | 'Compliant' | 'Listed' | 'In progress';
const STATUS_STYLES: Record<CertStatus, string> = {
  Certified: 'bg-[#EA580C]/15 text-[#F97316] ring-[#EA580C]/30',
  Aligned: 'bg-white/10 text-neutral-200 ring-white/20',
  Compliant: 'bg-white/10 text-neutral-200 ring-white/20',
  Listed: 'bg-white/10 text-neutral-200 ring-white/20',
  'In progress': 'bg-white/[0.04] text-neutral-500 ring-white/10'
};
const COMPLIANCE: {
  standard: string;
  badge: string;
  status: CertStatus;
  coverage: string;
  documentation: string;
}[] = [{
  standard: 'SOC 2 Type 1',
  badge: 'SOC',
  status: 'In progress',
  coverage: 'Aligning controls to SOC 2 criteria; formal Type 1 attestation to follow.',
  documentation: 'Available after certification.'
}, {
  standard: 'GDPR',
  badge: 'GDPR',
  status: 'Aligned',
  coverage: 'EU data-protection requirements met across processing and storage.',
  documentation: 'DPA available on request.'
}, {
  standard: 'CCPA',
  badge: 'CCPA',
  status: 'Compliant',
  coverage: 'California Consumer Privacy Act requirements met.',
  documentation: 'Privacy documentation available.'
}, {
  standard: 'CASA Tier 2',
  badge: 'CASA',
  status: 'In progress',
  coverage: 'Cloud Application Security Assessment — the required tier for restricted Google API access; assessment underway.',
  documentation: 'Attestation available on completion.'
}, {
  standard: 'Slack App Directory',
  badge: 'SLACK',
  status: 'In progress',
  coverage: 'Submitted to the Slack Marketplace; under review by Slack.',
  documentation: 'Listing available once approved.'
}, {
  standard: 'ISO 27001',
  badge: 'ISO',
  status: 'In progress',
  coverage: 'ISMS controls implementation and evidence collection underway.',
  documentation: 'Controls overview today; evidence after certification.'
}];
const DOES = [{
  title: 'Encrypts everything',
  body: 'TLS 1.2+ in transit. AES-256 at rest. Secrets held in dedicated, access-logged vaults.'
}, {
  title: 'Authenticates with SSO',
  body: 'SAML SSO across Okta, Entra ID, Google Workspace, OneLogin, and any SAML 2.0 IdP.'
}, {
  title: 'Offers data residency',
  body: 'EU-hosted by default (Hetzner, EU). Regional data residency available on Enterprise contracts.'
}, {
  title: 'Revokes instantly',
  body: 'Admins can disconnect any integration, pause any user, or kill a running task in one click.'
}];
const DOES_NOT = [{
  title: 'Train on your data',
  body: 'Your conversations and files never enter a training set — not ours, not our model providers’.'
}, {
  title: 'Read your secrets',
  body: 'API keys and tokens are injected at execution time by the tool gateway. The model never sees them.'
}, {
  title: 'Act without approval',
  body: 'Money moves, code pushes, and customer emails wait for your explicit approval in Slack.'
}, {
  title: 'Share across workspaces',
  body: 'Skills, integrations, and memory are walled off per workspace. No cross-tenant access.'
}];
const RISK_CONTROLS = [{
  icon: ShieldCheck,
  title: 'Prompt-injection defense',
  body: 'Untrusted content is rendered as data, not commands. High-risk tools sit behind human approval — an injection still can’t move money or push code.'
}, {
  icon: ServerCog,
  title: 'Named providers, no-training contracts',
  body: 'Inference runs on OpenAI, Anthropic, and Google. Each is on our public sub-processor list with a no-training agreement for Giga traffic.'
}, {
  icon: Database,
  title: 'Workspace isolation',
  body: 'Every workspace runs in a sandboxed execution environment. Skills, integrations, and memory are scoped to one tenant — what happens in your Slack stays in your Slack.'
}];
const DATA_PRINCIPLES = [{
  n: '01',
  title: 'Store only what we need',
  body: 'We keep the logs and session data required to make Giga work well — and nothing more. No silent data hoarding.'
}, {
  n: '02',
  title: 'We don’t train a model',
  body: 'Some agent companies quietly use your data to train. We don’t. We provide a service and ask you to pay for it.'
}, {
  n: '03',
  title: 'Your data is encapsulated',
  body: 'Your data and another customer’s data can never touch. Tenants are isolated, and your keys are stored securely, never exposed to the model.'
}];
/* ── Page ─────────────────────────────────────────────────────────── */
export function Security() {
  return <div className="w-full bg-[#FAF8F4] text-[#2B211A] font-body" data-id="element-11745" data-mp="3vga4kjl8f8wd">
      {/* ===== HERO (dark) ===== */}
      <section className="relative w-full bg-[#1C1510] overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24" data-id="element-11746" data-mp="3gizm2qa692r5">
        <div className="absolute inset-0 dot-matrix-dark opacity-70 pointer-events-none" data-id="element-11747" data-mp="3lhud2fialxmc" />
        <motion.div aria-hidden="true" className="pointer-events-none absolute -top-40 right-0 w-[640px] h-[640px] rounded-full bg-[#F97316]/[0.14] blur-[150px]" animate={{
        x: [0, 30, 0],
        y: [0, 24, 0],
        opacity: [0.6, 1, 0.6]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-11748" data-mp="37ib63q17wsbm" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-11749" data-mp="1apj4585vzi7p">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-11750" data-mp="18cfhhwvdkqyu">
            {/* Copy */}
            <div data-id="element-11751" data-mp="1487srzox3b0m">
              <motion.span initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="font-mono text-xs tracking-widest uppercase text-neutral-400 mb-5 block" data-id="element-11752" data-mp="14bvmayud3xfc">
                Security &amp; compliance — built in from day one
              </motion.span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="font-title font-bold text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05] mb-6" data-id="element-11753" data-mp="1n00klqs92q6r">
                Security you can hand to{' '}
                <span className="text-[#F97316]" data-id="element-11754" data-mp="25zei2ip6uj20">your compliance team.</span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-base md:text-lg text-neutral-400 leading-relaxed max-w-lg mb-8" data-id="element-11755" data-mp="2daj8k7pdw3u9">
                Your credentials never touch the model. Every sensitive action
                waits for your approval. And your data never trains a model —
                not ours, not anyone’s.
              </motion.p>

              {/* Badge row */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="flex flex-wrap gap-2.5" data-id="element-11756" data-mp="316f7nqmcvexm">
                {['SOC 2', 'GDPR', 'CCPA', 'CASA Tier 2'].map(b => <span key={b} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/[0.04] ring-1 ring-white/10 text-sm font-medium text-neutral-200" data-id="element-11757" data-mp="3fv1m7q45s2ns">
                    <ShieldCheck size={14} className="text-[#F97316]" data-id="element-11758" data-mp="92pg9az3jkxe" />
                    {b}
                  </span>)}
              </motion.div>
            </div>

            {/* Tactile editorial panel */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block" data-id="element-11759" data-mp="j1oowtjv3gke">
              <div className="relative aspect-[4/3] rounded-3xl bg-[#241A12] ring-1 ring-white/10 overflow-hidden" data-id="element-11760" data-mp="3hlerz6dw56z7">
                <div className="absolute inset-0 dot-matrix-dark opacity-50" data-id="element-11761" data-mp="4x064ljw98y" />
                {/* binary murmur */}
                <div className="absolute inset-0 p-6 font-mono text-[10px] leading-relaxed text-white/[0.06] select-none overflow-hidden" data-id="element-11762" data-mp="3rggju9zjwn5h">
                  {Array.from({
                  length: 22
                }).map((_, i) => <div key={i} className="whitespace-nowrap" data-id="element-11763" data-mp="1ebjthdh9x521" data-mpi={i}>
                      01010011 01100101 01100011 01110101 01110010 01100101
                      00100000 01100010 01111001 00100000 01100100 01100101
                    </div>)}
                </div>
                {/* central lock motif */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-11764" data-mp="19c786x61w61k">
                  <div className="relative" data-id="element-11765" data-mp="1x21lffua7wmc">
                    <div className="absolute inset-0 -m-8 rounded-full bg-[#F97316]/10 blur-2xl" data-id="element-11766" data-mp="1x3497ewb5iy1" />
                    <div className="relative w-28 h-28 rounded-3xl bg-[#1C1510] ring-1 ring-white/10 flex items-center justify-center shadow-2xl shadow-black/40" data-id="element-11767" data-mp="22030zqsi1dt2">
                      <Lock size={44} className="text-[#F97316]" strokeWidth={1.75} data-id="element-11768" data-mp="1t07pfkv17gsi" />
                    </div>
                  </div>
                </div>
                {/* vintage stamp accent */}
                <div className="absolute bottom-5 left-5 w-16 h-20 rounded-sm bg-[#EA580C] rotate-[-8deg] ring-4 ring-[#F5E9DC] shadow-lg flex items-center justify-center" data-id="element-11769" data-mp="hl17ofmf46br">
                  <KeyRound size={26} className="text-[#1C1510]" data-id="element-11770" data-mp="3iwqm8p6uv3lv" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== YOU'RE IN CONTROL (cream) ===== */}
      <section className="w-full bg-[#FAF8F4] py-16 md:py-28 border-b border-neutral-200" data-id="element-11771" data-mp="3e3e7r7vw4d6u">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-11772" data-mp="2z6n8cym7v1h8">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="font-title font-bold text-3xl md:text-5xl tracking-tight mb-12 md:mb-16" data-id="element-11773" data-mp="2z52elb7q7mkf">
            You’re in control.
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-dashed border-neutral-300" data-id="element-11774" data-mp="ofnfapgj5ka2">
            {CONTROL_PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return <motion.div key={p.n} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="py-8 md:py-10 md:px-8 md:first:pl-0 border-b md:border-b-0 md:border-r last:border-r-0 last:border-b-0 border-dashed border-neutral-300" data-id="element-11775" data-mp="13if1hjl3itrc" data-mpi={i}>
                  <div className="flex items-center justify-between mb-8 md:mb-12" data-id="element-11776" data-mp="1h9a3qd13acly">
                    <span className="font-title italic font-bold text-lg text-[#EA580C]" data-id="element-11777" data-mp="1v8jhl61toumr">
                      {p.n}
                    </span>
                    <Icon size={18} className="text-[#EA580C]" data-id="element-11778" data-mp="1r89fw5l2cqdp" />
                  </div>
                  <h3 className="font-title font-bold text-xl md:text-2xl mb-2" data-id="element-11779" data-mp="vjxp0qete9o">
                    {p.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed" data-id="element-11780" data-mp="3t04af8f6ue8p">{p.body}</p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ===== COMPLIANCE TABLE (dark) ===== */}
      <section className="w-full bg-[#1C1510] py-16 md:py-28 relative overflow-hidden" data-id="element-11781" data-mp="20yunt797wth7">
        <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-11782" data-mp="25ujqvkkg4si9" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-11783" data-mp="2aw914xzuh25a">
          <div className="max-w-2xl mb-12 md:mb-16" data-id="element-11784" data-mp="2evhrdsvoehw3">
            <span className="font-mono text-xs tracking-widest uppercase text-[#F97316] mb-4 block" data-id="element-11785" data-mp="2jvmazrrvrsqj">
              Compliance
            </span>
            <h2 className="font-title font-bold text-3xl md:text-5xl text-white tracking-tight leading-[1.1]" data-id="element-11786" data-mp="2onnkj5xnz5cv">
              Independently audited. Continuously verified.
            </h2>
            <p className="text-neutral-400 mt-5 leading-relaxed" data-id="element-11787" data-mp="2ylbis5uhauzs">
              The audit reports are real, the controls are monitored, and the
              next audit is always on the calendar.
            </p>
          </div>

          {/* Column headers (desktop) */}
          <div className="hidden lg:grid grid-cols-[1.2fr_0.8fr_2fr_1.4fr] gap-6 px-6 pb-4 border-b border-white/10 font-mono text-[11px] uppercase tracking-wider text-neutral-500" data-id="element-11788" data-mp="mxeyu8ltu2x8">
            <span data-id="element-11789" data-mp="hzwyv7psey1d">Standard</span>
            <span data-id="element-11790" data-mp="czitjxfkm271">Status</span>
            <span data-id="element-11791" data-mp="d0k29v946cll">Coverage</span>
            <span data-id="element-11792" data-mp="d1bztbm9ukei">Documentation</span>
          </div>

          <div className="flex flex-col" data-id="element-11793" data-mp="1alg2b345et5s">
            {COMPLIANCE.map((c, i) => <motion.div key={c.standard} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.06
          }} className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr_2fr_1.4fr] gap-3 lg:gap-6 px-5 lg:px-6 py-6 rounded-2xl lg:rounded-none border border-white/10 lg:border-0 lg:border-b lg:border-white/10 bg-white/[0.03] lg:bg-transparent mb-3 lg:mb-0 hover:bg-white/[0.04] transition-colors" data-id="element-11794" data-mp="15n4sgsdusink" data-mpi={i}>
                {/* Standard */}
                <div className="flex items-center gap-3" data-id="element-11795" data-mp="1d7fghs5ve5sr">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0 text-[#1C1510] font-mono text-[9px] font-bold leading-none text-center" data-id="element-11796" data-mp="z74p8o0nep72">
                    {c.badge}
                  </div>
                  <span className="text-white font-semibold" data-id="element-11797" data-mp="1rvinn4vnfnj5">{c.standard}</span>
                </div>
                {/* Status */}
                <div className="flex items-center" data-id="element-11798" data-mp="1i577mjyoxnvw">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ring-1 ${STATUS_STYLES[c.status]}`} data-id="element-11799" data-mp="1i4eb0btg4erh">
                    {c.status}
                  </span>
                </div>
                {/* Coverage */}
                <p className="text-sm text-neutral-300 leading-relaxed" data-id="element-11800" data-mp="2fkl3q8amx8bj">
                  {c.coverage}
                </p>
                {/* Documentation */}
                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-11801" data-mp="441a7dbztcjq">
                  {c.documentation}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ===== DOES / DOES NOT (cream) ===== */}
      <section className="w-full bg-[#FAF8F4] py-16 md:py-28" data-id="element-11802" data-mp="8wkne96rq5o0">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-11803" data-mp="8yfbtxh90fwu">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16" data-id="element-11804" data-mp="3hd5utljr4qp0">
            <span className="font-mono text-xs tracking-widest uppercase text-[#EA580C] mb-4 block" data-id="element-11805" data-mp="3cdkr6xtmzu5y">
              Data handling
            </span>
            <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight leading-[1.1]" data-id="element-11806" data-mp="3qe3y21md8u6j">
              What Giga does. What Giga does not.
            </h2>
            <p className="text-neutral-600 mt-5 leading-relaxed" data-id="element-11807" data-mp="15ljzy0edxtm0">
              Exactly what Giga touches — and what it never does.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10" data-id="element-11808" data-mp="3ufgrrxmb50vi">
            {/* Does */}
            <div data-id="element-11809" data-mp="395oduttro10">
              <div className="flex items-center gap-2.5 mb-5" data-id="element-11810" data-mp="38npeqfs2vpe">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#EA580C] text-white" data-id="element-11811" data-mp="3b09pa90di5m">
                  <Check size={16} strokeWidth={3} data-id="element-11812" data-mp="c9lm0nnw00xy" />
                </span>
                <span className="font-title font-bold text-lg" data-id="element-11813" data-mp="c7hm0gt3zm2y">Does</span>
              </div>
              <div className="flex flex-col gap-3" data-id="element-11814" data-mp="3bmw4qwtahpzm">
                {DOES.map((d, i) => <motion.div key={d.title} initial={{
                opacity: 0,
                y: 12
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.06
              }} className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-neutral-200" data-id="element-11815" data-mp="3fr3tuv3nt7yd" data-mpi={i}>
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C]/15 text-[#EA580C] shrink-0" data-id="element-11816" data-mp="2pgjv4p52t4jj">
                      <Check size={13} strokeWidth={3} data-id="element-11817" data-mp="2kis4rzk2xqhg" />
                    </span>
                    <div data-id="element-11818" data-mp="2fhm9pv2y5r4r">
                      <h3 className="font-semibold text-[#2B211A] mb-1" data-id="element-11819" data-mp="2fgsz2j0vahhc">
                        {d.title}
                      </h3>
                      <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-11820" data-mp="2bj55bjawuur1">
                        {d.body}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Does not */}
            <div data-id="element-11821" data-mp="1wsnytljf5ena">
              <div className="flex items-center gap-2.5 mb-5" data-id="element-11822" data-mp="1ws5zuh5dgmbo">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-neutral-300 text-neutral-600" data-id="element-11823" data-mp="1rtuo20vio1qt">
                  <X size={16} strokeWidth={3} data-id="element-11824" data-mp="1nqert5nd03en" />
                </span>
                <span className="font-title font-bold text-lg text-neutral-500" data-id="element-11825" data-mp="1it6a1664i1dx">
                  Does not
                </span>
              </div>
              <div className="flex flex-col gap-3" data-id="element-11826" data-mp="36n02rv7fusgd">
                {DOES_NOT.map((d, i) => <motion.div key={d.title} initial={{
                opacity: 0,
                y: 12
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.06
              }} className="flex items-start gap-3 p-5 rounded-2xl bg-[#F1ECE3] border border-neutral-200" data-id="element-11827" data-mp="3bn4hbfosdd3y" data-mpi={i}>
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-neutral-300 text-neutral-600 shrink-0" data-id="element-11828" data-mp="2e0nnikf9hhmy">
                      <X size={13} strokeWidth={3} data-id="element-11829" data-mp="2izi3zfprau3w" />
                    </span>
                    <div data-id="element-11830" data-mp="250s6w02xsufj">
                      <h3 className="font-semibold text-neutral-700 mb-1" data-id="element-11831" data-mp="2508x56nfxccl">
                        {d.title}
                      </h3>
                      <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-11832" data-mp="1qcid89qa90bt">
                        {d.body}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== AI RISK CONTROLS (dark) ===== */}
      <section className="w-full bg-[#1C1510] py-16 md:py-28 relative overflow-hidden" data-id="element-11833" data-mp="1vblhhryfv2iq">
        <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-11834" data-mp="29b35440009qi" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-11835" data-mp="24d1khemxc2cx">
          <div className="max-w-2xl mb-12 md:mb-16" data-id="element-11836" data-mp="2j1fwog68d365">
            <span className="font-mono text-xs tracking-widest uppercase text-[#F97316] mb-4 block" data-id="element-11837" data-mp="2j04irnj2mf1b">
              AI safety
            </span>
            <h2 className="font-title font-bold text-3xl md:text-5xl text-white tracking-tight leading-[1.1]" data-id="element-11838" data-mp="2s23z0dm9hf37">
              AI brings new risks. We built for them.
            </h2>
            <p className="text-neutral-400 mt-5 leading-relaxed" data-id="element-11839" data-mp="2n0y5weoovpt5">
              An AI coworker introduces attack surfaces traditional SaaS
              doesn’t. Three controls keep that surface small.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-11840" data-mp="39jowsqpncepg">
            {RISK_CONTROLS.map((r, i) => {
            const Icon = r.icon;
            return <motion.div key={r.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="p-7 md:p-8 rounded-3xl bg-white/[0.03] ring-1 ring-white/10" data-id="element-11841" data-mp="34lnei801ojf1" data-mpi={i}>
                  <div className="w-12 h-12 rounded-2xl bg-[#EA580C]/15 ring-1 ring-[#EA580C]/30 flex items-center justify-center mb-6" data-id="element-11842" data-mp="2ls9ngdzgcx4p">
                    <Icon size={22} className="text-[#F97316]" data-id="element-11843" data-mp="1t671p0rj4blq" />
                  </div>
                  <h3 className="font-title font-bold text-xl text-white mb-3" data-id="element-11844" data-mp="8nmp1als5w9l">
                    {r.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed text-sm md:text-base" data-id="element-11845" data-mp="dfejhw824m50">
                    {r.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ===== DATA & ENGINEERING PRINCIPLES (cream) ===== */}
      <section className="w-full bg-[#FAF8F4] py-16 md:py-28 border-t border-neutral-200" data-id="element-11846" data-mp="rdw43l2o1iip">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-11847" data-mp="wdqvdpqcsa4a">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20" data-id="element-11848" data-mp="wb4l3ic2vgid">
            {/* Heading + tactile motif */}
            <div className="lg:sticky lg:top-32 self-start" data-id="element-11849" data-mp="nc136hond75g">
              <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight leading-[1.05] mb-8" data-id="element-11850" data-mp="nenacg7mlkn5">
                Giga’s data &amp; engineering principles.
              </h2>
              <div className="relative w-full max-w-xs aspect-square" data-id="element-11851" data-mp="3842wgxvwgl6g">
                <div aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] aspect-square rounded-full opacity-60" style={{
                backgroundImage: 'radial-gradient(circle, #d6d3d1 1.25px, transparent 1.25px)',
                backgroundSize: '14px 14px',
                WebkitMaskImage: 'radial-gradient(circle, black 35%, transparent 70%)',
                maskImage: 'radial-gradient(circle, black 35%, transparent 70%)'
              }} data-id="element-11852" data-mp="1dli19o03vtr6" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-11853" data-mp="pvxfkxckiie7">
                  <div className="w-28 h-28 rounded-3xl bg-white ring-1 ring-neutral-200 shadow-xl shadow-orange-900/5 flex items-center justify-center" data-id="element-11854" data-mp="kxchb3gvm8se">
                    <ScanEye size={42} className="text-[#EA580C]" strokeWidth={1.75} data-id="element-11855" data-mp="gydf3qmj4q4n" />
                  </div>
                </div>
              </div>
            </div>

            {/* Numbered list */}
            <div className="flex flex-col" data-id="element-11856" data-mp="2b87c8ncsdgp6">
              {DATA_PRINCIPLES.map((p, i) => <motion.div key={p.n} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="flex gap-5 md:gap-8 py-8 border-b border-dashed border-neutral-300 first:pt-0 last:border-b-0" data-id="element-11857" data-mp="3t7ezgglpplld" data-mpi={i}>
                  <span className="font-title italic font-bold text-xl text-[#EA580C] shrink-0 pt-1" data-id="element-11858" data-mp="3abf2erdvnrux">
                    {p.n}
                  </span>
                  <div data-id="element-11859" data-mp="3j8z94fuiegcx">
                    <h3 className="font-title font-bold text-xl md:text-2xl mb-3" data-id="element-11860" data-mp="3j8fsd2glhy0h">
                      {p.title}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed" data-id="element-11861" data-mp="yjrwdg35g302">{p.body}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CLOSING CTA (dark) ===== */}
      <section className="w-full bg-[#1C1510] py-16 md:py-24 relative overflow-hidden" data-id="element-11862" data-mp="1228obssmqqhr">
        <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-11863" data-mp="x3e6at2hl5yp" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-11864" data-mp="1b2dslu2j18rp">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 rounded-3xl bg-white/[0.03] ring-1 ring-white/10 p-8 md:p-12" data-id="element-11865" data-mp="170sds36fmkev">
            <div className="max-w-xl" data-id="element-11866" data-mp="1kygx36kupjht">
              <h2 className="font-title font-bold text-2xl md:text-4xl text-white tracking-tight mb-3 leading-tight" data-id="element-11867" data-mp="1g1i1skpgb6i1">
                Questions for your security review?
              </h2>
              <p className="text-neutral-400 leading-relaxed" data-id="element-11868" data-mp="1pr1lfskzn4dy">
                Request our compliance pack, a DPA, or a deep-dive with our
                team. We’ll get your procurement unblocked fast.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-11869" data-mp="1ysrc2w71pzao">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-7 py-3.5 rounded-full font-bold transition-colors" data-id="element-11870" data-mp="2idrmzd78qv1e">
                Talk to our team
                <ArrowRight size={16} data-id="element-11871" data-mp="29bs38d4udcur" />
              </Link>
              <WaitlistCTA className="inline-flex items-center justify-center bg-white/[0.06] hover:bg-white/10 ring-1 ring-white/10 text-white px-7 py-3.5 rounded-full font-medium transition-colors" data-id="element-11872" data-mp="2def04sgddnyt">
                Try Giga for free
              </WaitlistCTA>
            </div>
          </div>
        </div>
      </section>
    </div>;
}